import * as React from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';

import CampaignDashboardInfo from 'modules/campaigns/components/dashboard/CampaignDashboardInfo';
import SubcampaignsByState from 'modules/subcampaigns/components/subcampaigns-by-state/SubcampaignsByState';
import TabsSection from 'components/tabs/tabs-section/TabsSection';
import NotificationDot from 'components/notification-dot/NotificationDot';
import PageSection from 'components/page/page-section/PageSection';

import { resetBulkOffer } from 'modules/subcampaigns/reducers/bulkOffer';
import { fetchUnreadConversations } from 'modules/messenger/reducers/messengerNotifications';

import { navigate } from 'libs/router';
import { askUserToConfirmRouteExitPromise } from 'libs/router/confirm-exit';

import { isDisableUnreadMessagesSelector } from 'selectors/disableUnreadMessages';

import { __t, applyTranslations } from 'utils/language';
import { PARTICIPANT_TYPES_ORDER, PARTICIPANT_TYPE_TO_STATE_MAP } from 'utils/campaignParticipants';

import './campaign-subcampaigns.scss';

import type { CampaignOverview, MessengerNotifications, Subcampaign } from 'types';

applyTranslations(require('./campaign-subcampaigns.i18n.json'));

type OwnProps = {
  campaignId: number,
  selectedParticipantsType: string,
  isEditMode: boolean,
  subcampaign?: Subcampaign,
  subcampaignOfferSortState?: string,
  onShowHelperPageStatus?: (a: boolean) => void
};

type StateProps = {
  overview: CampaignOverview | undefined | null,
  currentOrganizationId: number | undefined | null,
  messengerNotifications: MessengerNotifications,
  disableUnreadMessages: boolean
};

type DispatchProps = {
  dispatchResetBulkOffer: () => Promise<any>,
  dispatchFetchUnreadConversations: (a: number) => Promise<void>,
  dispatchAskUserToConfirmRouteExit: () => Promise<'next' | 'cancel'>
};

type Props = OwnProps & StateProps & DispatchProps;

class CampaignSubcampaigns extends React.Component<Props> {
  componentDidMount(): void {
    const {
      currentOrganizationId,
      disableUnreadMessages,
      dispatchFetchUnreadConversations
    } = this.props;

    if (!disableUnreadMessages && currentOrganizationId) {
      dispatchFetchUnreadConversations(currentOrganizationId);
    }
  }

  componentDidUpdate(prevProps: Props): void {
    const {
      currentOrganizationId,
      disableUnreadMessages,
      dispatchFetchUnreadConversations
    } = this.props;

    if (!disableUnreadMessages && currentOrganizationId && prevProps.currentOrganizationId !== currentOrganizationId) {
      dispatchFetchUnreadConversations(currentOrganizationId);
    }
  }

  handleTabClicked = async (identifier?: string | null) => {
    const { campaignId, dispatchResetBulkOffer, dispatchAskUserToConfirmRouteExit } = this.props;

    const status = await dispatchAskUserToConfirmRouteExit();

    if (status === 'cancel') {
      return;
    }

    dispatchResetBulkOffer();

    navigate.to(`/campaigns/${campaignId}/participants/${identifier || PARTICIPANT_TYPES_ORDER[0]}`);
  };

  render() {
    const {
      campaignId,
      selectedParticipantsType,
      overview,
      subcampaign,
      messengerNotifications,
      subcampaignOfferSortState,
      isEditMode,
      onShowHelperPageStatus
    } = this.props;

    const tabs = PARTICIPANT_TYPES_ORDER.map(type => {
      const overviewGroup = get(overview, PARTICIPANT_TYPE_TO_STATE_MAP[type].overviewName, null);
      const count = overviewGroup && overviewGroup.creators || 0;
      const conversationExternalIdList = overviewGroup && overviewGroup.conversationExternalIdList || [];
      const notificationsCount = conversationExternalIdList.reduce((sum, externalId) => {
        if (messengerNotifications.unreadChatsMap[externalId]) {
          return sum + 1;
        }
        return sum;
      }, 0);

      return ({
        identifier: type,
        label: (
          <div className="campaign-subcampaigns__notification">
            {`${__t(`campaign_subcampaigns.tabs.${type}`)} (${count})`}
            &nbsp;
            {
              notificationsCount > 0 &&
                <div className="campaign-subcampaigns__notification-dot">
                  <NotificationDot />
                </div>
            }
          </div>
        )
      });
    });

    return (
      <section>
        <CampaignDashboardInfo pageType="participants" />
        <PageSection
          padding="none"
          style="none"
          title={__t('campaign_subcampaigns.tabs_heading')}
        >
          <TabsSection
            withShadow
            clickableActiveTab
            items={tabs}
            activeTab={selectedParticipantsType}
            onClick={this.handleTabClicked}
          />
        </PageSection>
        <SubcampaignsByState
          campaignId={campaignId}
          subcampaign={subcampaign}
          selectedParticipantsType={selectedParticipantsType}
          subcampaignOfferSortState={subcampaignOfferSortState}
          isEditMode={isEditMode}
          onShowHelperPageStatus={onShowHelperPageStatus}
        />
      </section>
    );
  }
}

const mapStateToProps = ({
  account,
  campaignOverview,
  messengerNotifications,
  organizations,
  runtimeConfig
}): StateProps => ({
  overview: campaignOverview.overview,
  currentOrganizationId: organizations.currentOrganizationId,
  messengerNotifications,
  disableUnreadMessages: isDisableUnreadMessagesSelector({ runtimeConfig, account })
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchResetBulkOffer: () => dispatch(resetBulkOffer()),
  dispatchFetchUnreadConversations: (organizationId) => dispatch(fetchUnreadConversations(organizationId)),
  dispatchAskUserToConfirmRouteExit: () => askUserToConfirmRouteExitPromise(dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignSubcampaigns);
